import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import tenantAdminService from '../../services/tenantAdminService';
import {
  isGateUnlocked,
  TENANT_ADMIN_GATE_EXPIRED_EVENT,
} from '../../utils/tenantAdminGate';
import TenantAdminGateDialog from './TenantAdminGateDialog';

interface TenantAdminGateProps {
  children: React.ReactNode;
}

const TenantAdminGate: React.FC<TenantAdminGateProps> = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [checking, setChecking] = useState(true);
  const [gateRequired, setGateRequired] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  const refreshAccessState = useCallback(async () => {
    try {
      setChecking(true);
      const status = await tenantAdminService.getAccessStatus();
      if (!status.gateRequired) {
        setGateRequired(false);
        setUnlocked(true);
        return;
      }

      setGateRequired(true);
      setUnlocked(isGateUnlocked());
    } catch {
      setGateRequired(true);
      setUnlocked(isGateUnlocked());
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    void refreshAccessState();
  }, [refreshAccessState]);

  useEffect(() => {
    const handleGateExpired = () => {
      setUnlocked(false);
      enqueueSnackbar('La sesión de administración de tenants expiró. Ingrese la contraseña nuevamente.', {
        variant: 'warning',
        preventDuplicate: true,
      });
    };

    window.addEventListener(TENANT_ADMIN_GATE_EXPIRED_EVENT, handleGateExpired);
    return () => window.removeEventListener(TENANT_ADMIN_GATE_EXPIRED_EVENT, handleGateExpired);
  }, [enqueueSnackbar]);

  if (checking) {
    return (
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: '#f5f5f5',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (gateRequired && !unlocked) {
    return <TenantAdminGateDialog onUnlocked={() => setUnlocked(true)} />;
  }

  return <>{children}</>;
};

export default TenantAdminGate;
